import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { toast } from "sonner";
import { format, subDays } from "date-fns";
import { es } from "date-fns/locale";
import { 
  Calendar, Users, DollarSign, Bell, Check, 
  ChevronLeft, ChevronRight, Filter, 
  TrendingUp, Package, FileText, Plus, Trash2, 
  LogOut, BarChart3, AlertTriangle, Share2, Settings, MapPin, Clock, Scissors, Globe
} from "lucide-react";

// 🌍 DICCIONARIO DEL SISTEMA DEMO
const TRANSLATIONS = {
  es: { dashboard: "Dashboard", appointments: "Citas", cuts: "Cortes", inventory: "Inventario", finances: "Finanzas", reports: "Informes", settings: "Ajustes", today_appointments: "Citas Hoy", today_cuts: "Cortes Hoy", today_income: "Ingresos Hoy", today_profit: "Ganancia Hoy", this_month: "Este Mes", income: "Ingresos", expenses: "Gastos", profit: "Ganancia", month_cuts: "Cortes del Mes", alerts: "Alertas", low_stock: "Stock bajo", pending_appointments: "Citas pendientes" }
};

const STATUS_LABELS = { pending: "Pendiente", confirmed: "Confirmada", completed: "Completada" };

const DemoDashboard = () => {
  const [activeSection, setActiveSection] = useState("dashboard");
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateRange, setDateRange] = useState({ start: format(subDays(new Date(), 30), "yyyy-MM-dd"), end: format(new Date(), "yyyy-MM-dd") });
  
  // 🌟 DATOS FALSOS PRECARGADOS PARA LA DEMO
  const [adminProfile] = useState({
    shop_name: "Barbershop (Modo Demo)", logo_url: "/logo192.png", currency: "USD", language: "es", slug: "demo-barbershop",
    services: [
      { id: "1", name: "Corte VIP", price: 25 },
      { id: "2", name: "Barba y Cejas", price: 15 }
    ]
  });

  const [appointments, setAppointments] = useState([
    { id: "1", time: "10:00", client_name: "Carlos Gómez", client_phone: "809-123-4567", service: "Corte VIP", price: 25, status: "pending", queue_position: 1 },
    { id: "2", time: "11:30", client_name: "Miguel Peña", client_phone: "809-987-6543", service: "Barba y Cejas", price: 15, status: "confirmed", queue_position: 2 }
  ]);

  const [cuts] = useState([
    { id: "1", date: format(new Date(), "yyyy-MM-dd"), time: "09:00", client_name: "José Pérez", service: "Corte VIP", price: 25, barber_name: "Erick" }
  ]);

  const [inventory] = useState([
    { id: "1", name: "Cera Moldeadora", category: "Productos", quantity: 12, min_quantity: 5 },
    { id: "2", name: "Gillette Platinum", category: "Herramientas", quantity: 2, min_quantity: 10 }
  ]);

  const [transactions] = useState([
    { id: "1", date: format(new Date(), "yyyy-MM-dd"), type: "income", category: "Servicio", description: "Corte - José Pérez", amount: 25 },
    { id: "2", date: format(new Date(), "yyyy-MM-dd"), type: "expense", category: "Insumos", description: "Compra de toallas", amount: 10 }
  ]);

  const stats = {
    today: { appointments: 5, cuts: 3, income: 75, profit: 65 },
    month: { income: 1250, expenses: 300, profit: 950, cuts: 85 },
    alerts: { low_stock: 1, pending_appointments: 2 }
  };

  const currentCurrency = adminProfile.currency;
  const t = (key) => TRANSLATIONS["es"][key] || key;

  // 🛡️ FUNCIONES TRAMPA (Muestran alertas en vez de ir a la base de datos)
  const handleSimulateAction = (actionName) => {
    toast.success(`¡${actionName} exitoso! (Modo Demo: No se guardó en la base de datos real)`);
  };

  const handleStatusChange = (id, status) => {
    setAppointments(appointments.map(apt => apt.id === id ? { ...apt, status } : apt));
    toast.success(`Estado cambiado a ${STATUS_LABELS[status]}. (Modo Demo)`);
  };

  const totalIncome = transactions.filter(t => t.type === "income").reduce((sum, t) => sum + t.amount, 0);
  const totalExpenses = transactions.filter(t => t.type === "expense").reduce((sum, t) => sum + t.amount, 0);
  const filteredAppointments = statusFilter === "all" ? appointments : appointments.filter(a => a.status === statusFilter);

  return (
    <div className="min-h-screen bg-[#09090b]">
      {/* 🚨 AVISO DE MODO DEMO */}
      <div className="bg-amber-500 text-black text-center py-2 font-bold text-sm uppercase tracking-widest px-4">
        Estás en el Modo Demostración. Los cambios no se guardarán.
      </div>

      <nav className="sticky top-0 z-50 backdrop-blur-xl bg-black/60 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={adminProfile.logo_url} alt="Logo" className="w-10 h-10 object-cover rounded-lg bg-zinc-900 border border-amber-500/50" />
            <span className="font-heading text-xl font-bold uppercase tracking-wider text-white truncate">
              {adminProfile.shop_name}
            </span>
          </div>
          <Link to="/" className="text-zinc-400 hover:text-white font-bold text-sm flex items-center">
            <LogOut className="w-4 h-4 mr-2" /> Salir de la Demo
          </Link>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-6">
        <div className="flex flex-wrap gap-2 mb-6">
          {[
            { id: "dashboard", label: t("dashboard"), icon: BarChart3 },
            { id: "appointments", label: t("appointments"), icon: Calendar },
            { id: "cuts", label: t("cuts"), icon: Users },
            { id: "inventory", label: t("inventory"), icon: Package },
            { id: "finances", label: t("finances"), icon: DollarSign },
          ].map(({ id, label, icon: Icon }) => (
            <Button key={id} onClick={() => setActiveSection(id)} className={`font-heading uppercase text-xs ${activeSection === id ? "bg-amber-500 text-black" : "bg-zinc-800 text-zinc-400 hover:text-white"}`}>
              <Icon className="w-4 h-4 mr-2" /> {label}
            </Button>
          ))}
        </div>

        {activeSection === "dashboard" && (
          <div className="space-y-6">
            <div className="bg-gradient-to-r from-amber-500/20 to-amber-500/5 border border-amber-500/30 p-6 rounded-lg">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <h3 className="font-heading text-lg font-bold uppercase text-amber-500 flex items-center gap-2"><Share2 className="w-5 h-5" /> Tu Link de Reservas</h3>
                  <p className="text-zinc-400 text-sm mt-1">Comparte este link con tus clientes para que reserven citas</p>
                </div>
                <div className="flex items-center gap-2">
                  <div className="bg-zinc-900 border border-zinc-700 px-4 py-3 text-white text-sm truncate rounded-md">booseenterprise.com/demo-barbershop</div>
                  <Button onClick={() => handleSimulateAction("Copiado")} className="h-11 bg-amber-500 text-black font-heading uppercase text-xs">Copiar</Button>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-zinc-900 border border-zinc-800 p-5 rounded-lg">
                <div className="flex items-center gap-2 mb-2"><Calendar className="w-4 h-4 text-amber-500" /><span className="text-zinc-500 text-xs uppercase">{t("today_appointments")}</span></div>
                <p className="font-heading text-3xl font-bold text-white">{stats.today.appointments}</p>
              </div>
              <div className="bg-zinc-900 border border-zinc-800 p-5 rounded-lg">
                <div className="flex items-center gap-2 mb-2"><DollarSign className="w-4 h-4 text-green-500" /><span className="text-zinc-500 text-xs uppercase">{t("today_income")}</span></div>
                <p className="font-heading text-2xl font-bold text-green-500">{stats.today.income} {currentCurrency}</p>
              </div>
              <div className="bg-zinc-900 border border-zinc-800 p-5 rounded-lg">
                <div className="flex items-center gap-2 mb-2"><TrendingUp className="w-4 h-4 text-amber-500" /><span className="text-zinc-500 text-xs uppercase">{t("today_profit")}</span></div>
                <p className="font-heading text-2xl font-bold text-amber-500">{stats.today.profit} {currentCurrency}</p>
              </div>
              <div className="bg-zinc-900 border border-zinc-800 p-5 rounded-lg">
                <div className="flex items-center gap-2 mb-2"><AlertTriangle className="w-4 h-4 text-red-500" /><span className="text-zinc-500 text-xs uppercase">{t("low_stock")}</span></div>
                <p className="font-heading text-3xl font-bold text-red-500">{stats.alerts.low_stock}</p>
              </div>
            </div>
          </div>
        )}

        {activeSection === "appointments" && (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            <div className="lg:col-span-4"><div className="bg-zinc-900 border border-zinc-800 p-4 rounded-lg"><CalendarComponent mode="single" selected={selectedDate} locale={es} className="text-white" /></div></div>
            <div className="lg:col-span-8 space-y-3">
              <h2 className="font-heading text-xl text-white uppercase font-bold mb-4">Citas de Hoy (Simulación)</h2>
              {filteredAppointments.map((apt) => (
                <div key={apt.id} className={`appointment-card ${apt.status} p-4 rounded-lg`}>
                  <div className="pl-4 flex justify-between items-center">
                    <div>
                      <div className="flex items-center gap-3 mb-2">
                        <span className="font-heading text-xl font-bold text-amber-500">{apt.time}</span>
                        <span className={`status-badge status-${apt.status}`}>{STATUS_LABELS[apt.status]}</span>
                      </div>
                      <p className="text-white font-semibold">{apt.client_name}</p>
                      <p className="text-zinc-500 text-sm">{apt.client_phone} • {apt.service}</p>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="font-heading text-lg font-bold text-white">{apt.price} {currentCurrency}</span>
                      {apt.status === "pending" && <Button onClick={() => handleStatusChange(apt.id, "confirmed")} size="icon" className="bg-green-500/10 text-green-500 hover:bg-green-500/20"><Check className="w-4 h-4" /></Button>}
                      {apt.status === "confirmed" && <Button onClick={() => handleStatusChange(apt.id, "completed")} size="icon" className="bg-blue-500/10 text-blue-500 hover:bg-blue-500/20"><Check className="w-4 h-4" /></Button>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Las demás secciones usan placeholders para la demo rápida */}
        {(activeSection === "cuts" || activeSection === "inventory" || activeSection === "finances") && (
          <div className="bg-zinc-900 border border-zinc-800 p-12 text-center rounded-lg">
            <h2 className="text-2xl font-heading text-white uppercase mb-4">Sección {activeSection}</h2>
            <p className="text-zinc-500 mb-6">En la versión completa, aquí verás y gestionarás todos tus datos reales en vivo.</p>
            <Button onClick={() => handleSimulateAction("Crear Registro")} className="bg-amber-500 text-black font-bold uppercase"><Plus className="w-4 h-4 mr-2" /> Agregar Dato (Demo)</Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default DemoDashboard;